import { Injectable, inject } from '@angular/core';
import { CatGraphServiceClient } from '../../../generated.commonjs/ApiServiceClientPb';
import {
  CatGraph,
  CatGraphSummary,
  GetCatGraphRequest,
  ListCatGraphsRequest,
} from '../../../generated.commonjs/cat_graph_pb';
import { AuthInterceptor } from '../auth.interceptor';
import { ClientLoggingService } from '../client-logging.service';
import { EvidenceCitationLinkService } from '../citations/evidence-citation-link.service';
import { EnvironmentService } from '../environment.service';
import { HighlightBox, highlightQueryParams } from '../highlight-regions';
import { ArchitecturalPlanPage } from '../models/architectural-plan-page.model';
import { extractFileIdFromPage } from '../page-path.util';
import { PdfCacheService } from '../pdf-cache.service';
import { PlanReviewerService } from '../plan-reviewer.service';
import { previewCommands, toPageBox } from '../project-search-results';
import { ChipDestination } from './cat-graph-evidence';
import { NormalizedBox } from '../citations/evidence-citation.model';

/** Where a chip leads, ready for the router or for a new tab. */
export type EvidenceLink =
  | { kind: 'internal'; commands: string[]; queryParams: Record<string, string | null> }
  | { kind: 'external'; href: string }
  | { kind: 'none'; reason: string };

/**
 * Reads a project's CAT graphs from the backend's `CatGraphService` (Issues #2525, #2986) and builds
 * the links of their evidence (Issue #2399).
 *
 * The picture comes from `GetCatGraphPicture`: the backend checks that the caller may read the
 * project, loads the stored graph and asks the cat-graph service to draw it. The frontend never
 * draws a graph itself.
 */
@Injectable({ providedIn: 'root' })
export class CatGraphService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private planReviewer = inject(PlanReviewerService);
  private pdfCache = inject(PdfCacheService);
  private linkService = inject(EvidenceCitationLinkService);
  private client: CatGraphServiceClient;

  constructor() {
    const host = `https://${this.environmentService.getApiServer()}:443`;
    this.client = new CatGraphServiceClient(host, null, { unaryInterceptors: [this.authInterceptor] });
  }

  /** The project's graphs, ordered by graph ID. */
  async list(projectId: string): Promise<CatGraphSummary[]> {
    const request = new ListCatGraphsRequest();
    request.setArchitecturalPlanId(projectId);
    const response = await this.client.listCatGraphs(request, null);
    return response.getGraphsList();
  }

  /** One stored graph: its boxes, gates and citations. */
  get(projectId: string, graphId: string): Promise<CatGraph> {
    return this.client.getCatGraph(this.request(projectId, graphId), null);
  }

  /** The service's picture of one stored graph, as SVG text. Not yet cleaned. */
  async picture(projectId: string, graphId: string): Promise<string> {
    const picture = await this.client.getCatGraphPicture(this.request(projectId, graphId), null);
    return picture.getSvg();
  }

  /**
   * The link for a chip. A plan page opens in the Preview with the chip's boxes highlighted, in the
   * Preview's existing link format: the boxes are converted to PDF points with the page's size from
   * the shared parsed PDF cache, as the project search does. When the page or its size cannot be
   * read, the page opens without a highlight.
   */
  async link(destination: ChipDestination): Promise<EvidenceLink> {
    if (destination.kind === 'none') return destination;
    if (destination.kind === 'citation') {
      const built = this.linkService.buildDestination(destination.citation);
      if (built.kind === 'internal') {
        return { kind: 'internal', commands: built.routerLink, queryParams: built.queryParams };
      }
      return built.kind === 'external' ? { kind: 'external', href: built.href } : built;
    }

    const { planId, fileId, pageNumber } = destination.citation.page;
    const commands = previewCommands(planId, fileId, pageNumber);
    if (destination.boxes.length === 0) return { kind: 'internal', commands, queryParams: {} };
    try {
      const plan = await this.planReviewer.getArchitecturalPlan(planId);
      const pages: ArchitecturalPlanPage[] = plan?.getPagesList?.() ?? [];
      const page = pages.find(
        (candidate: ArchitecturalPlanPage) =>
          candidate.getPageNumber() === pageNumber && (extractFileIdFromPage(candidate) ?? '1') === (fileId || '1')
      );
      const parsed = page ? await this.pdfCache.getParsedPage(page) : null;
      if (!parsed) return { kind: 'internal', commands, queryParams: {} };
      const viewport = parsed.page.getViewport({ scale: 1 });
      const boxes = destination.boxes
        .map(box => pointBox(box, viewport.width, viewport.height))
        .filter((box): box is HighlightBox => box !== null);
      const regions = boxes.map(box => ({ box }));
      return { kind: 'internal', commands, queryParams: highlightQueryParams(regions, parsed.page.rotate) };
    } catch (error) {
      ClientLoggingService.log('WARN', '[CatGraphService] Opening the page without a highlight:', error);
      return { kind: 'internal', commands, queryParams: {} };
    }
  }

  private request(projectId: string, graphId: string): GetCatGraphRequest {
    const request = new GetCatGraphRequest();
    request.setArchitecturalPlanId(projectId);
    request.setGraphId(graphId);
    return request;
  }
}

/**
 * A box in fractions of the page as a box in PDF points, padded and kept on the page. The search's
 * conversion takes boxes in thousandths, as `[yMin, xMin, yMax, xMax]`, so the box is written that
 * way and the padding stays the same in both.
 */
export function pointBox(box: NormalizedBox, width: number, height: number): HighlightBox | null {
  return toPageBox(
    [
      box.y * 1000,
      box.x * 1000,
      Math.min(1000, (box.y + box.height) * 1000),
      Math.min(1000, (box.x + box.width) * 1000),
    ],
    width,
    height
  );
}
